/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  FC,
  HTMLInputTypeAttribute,
  ReactNode,
  useEffect,
  useRef,
  useState,
} from 'react'
import {
  TextField,
  Box,
  useTheme,
  Theme,
  BoxProps,
  SxProps,
  InputAdornment,
} from '@mui/material'
import {
  FieldError,
  RegisterOptions,
  UseFormRegister,
  useController,
  useFormContext,
} from 'react-hook-form'
import { LoadedFormLabel } from '../display'
import {
  formatCurrency,
  formatPercent,
  formatNumber,
  getThousandsSeparator,
} from 'src/components/routes/_common/helpers'
import { regExps } from '$constants'
import {
  InlineInputIcon,
  InputWithInlineIcon,
} from './CustomInputBaseComponent'
import { transformTextFieldNumberOnChange } from './helper'

type LoadedTextFieldProps = {
  name: string
  label?: ReactNode
  placeholder?: string
  type?: HTMLInputTypeAttribute
  multiline?: boolean
  onChange?: (...args: any) => any
  onBufferingChange?: (...args: any) => any
  onBlur?: (...args: any) => any
  onFocus?: (...args: any) => any
  onKeyDown?: (...args: any) => any
  labelBoxProps?: BoxProps
  labelPosition?: 'left' | 'above'
  textFieldSxProps?: SxProps<Theme>
  additiveTextFieldSxProps?: SxProps<Theme>
  containerSxProps?: SxProps<Theme>
  startAdornment?: ReactNode
  endAdornment?: ReactNode
  variant?: 'input' | 'currency' | 'percent' | 'number' | 'hour'
  size?: 'small' | 'medium' | 'large'
  labelSxProps?: SxProps<Theme>
  fullWidth?: boolean
  inputProps?: any
  isInGrid?: boolean
  tabIndex?: number
  // This could be a 0-100 based on the NumberParser but we shouldn't need more than these DP in a textfield.
  // So limiting to these options
  numberPrecision?: 0 | 1 | 2 | 3
  autoComplete?: string
  /**
   * Allow an icon to be shown inside the input, immediately to the right of the input content.
   * The tooltip message will be displayed when the icon is hovered over.
   */
  inlineInputIcon?: InlineInputIcon
  hasFocus?: boolean
  isFocused?: boolean
  onlyPostiveNumbers?: boolean
} & RegisterOptions<any>

const LoadedTextFieldBufferingTimeout: number = 300

const LoadedTextField: FC<LoadedTextFieldProps> = (props) => {
  const { value } = props
  // If the value is undefined, then the component is being used in a form
  return value === undefined ? (
    <FormTextField {...props} />
  ) : (
    <SelfContainedTextField {...props} />
  )
}

export default LoadedTextField

const FormTextField: FC<LoadedTextFieldProps> = (props) => {
  const {
    name,
    onChange,
    onBlur,
    disabled,
    variant,
    numberPrecision,
    isInGrid,
    onlyPostiveNumbers,
  } = props
  const { control, register, setValue } = useFormContext()
  const {
    field: { value },
    fieldState: { error },
  } = useController({ control, name })
  // format the currency, percent, or number value when the component is loaded
  useEffect(() => {
    if (variant === 'currency' && value != null) {
      setValue(name, formatCurrency(+value, true))
    } else if (variant === 'percent' && value != null) {
      setValue(name, formatPercent(+value, true))
    } else if (variant === 'number' && value != null) {
      setValue(name, formatNumber(+value, numberPrecision))
    } else if (variant === 'hour' && value != null) {
      setValue(name, formatPercent(+value, true))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const onChangeCurrencyHandler = (value: string) => {
    //This prevents the user to insert letters and symbols in the input
    const newValue = value.replace(regExps.currencyInput, '')

    return transformTextFieldNumberOnChange(newValue, onlyPostiveNumbers)
  }

  const onChangePercentHandler = (value: string) => {
    //This prevents the user to insert letters and symbols in the input
    const newValue = value.replace(regExps.percentInput, '')

    return transformTextFieldNumberOnChange(newValue, onlyPostiveNumbers)
  }

  const onChangeNumberHandler = (value: string) => {
    //This prevents the user to insert letters and symbols in the input
    const newValue = value.replace(regExps.numberInput, '')

    return transformTextFieldNumberOnChange(newValue, onlyPostiveNumbers)
  }

  const onBlurCurrencyHandler = (value: string) => {
    // a currency type field will have an adornment showing the currency
    // symbol, we don't actually want to format the input value as currency
    return value ? formatNumber(value, 2) : ''
  }

  const onBlurPercentHandler = (value: string) => {
    return value ? formatPercent(+value, true) : ''
  }

  const onBlurNumberHandler = (value: string) => {
    return value ? formatNumber(+value, numberPrecision) : ''
  }

  const [isFocused, setIsFocused] = useState<boolean>(false)

  const onChangeHandler = (event: { target: { value: string } }) => {
    let newValue: string = ''
    if (variant === 'currency') {
      newValue = onChangeCurrencyHandler(event.target.value)
    } else if (variant === 'percent') {
      newValue = onChangePercentHandler(event.target.value)
    } else if (variant === 'number') {
      newValue = onChangeNumberHandler(event.target.value)
    } else if (variant === 'hour') {
      newValue = onChangePercentHandler(event.target.value)
    }

    if (onChange) {
      onChange(event)
    }

    setValue(name, newValue)
  }

  const onBlurHandler = (event: { target: { value: string } }) => {
    let newValue: string = ''
    if (variant === 'currency') {
      newValue = onBlurCurrencyHandler(event.target.value)
    } else if (variant === 'percent') {
      newValue = onBlurPercentHandler(event.target.value)
    } else if (variant === 'number') {
      newValue = onBlurNumberHandler(event.target.value)
    } else if (variant === 'hour') {
      newValue = onBlurPercentHandler(event.target.value)
    }

    if (onBlur) {
      onBlur(event)
    }

    setValue(name, newValue)
    setIsFocused(false)
  }

  const onFocusHandler = () => {
    setIsFocused(true)
    // When the variant is currency, we want to remove any thousands separators
    // that are present in the formatted value in the focused input. If we
    // don't do this, when the user starts editing a value that has a thousands
    // separator present the cursor will jump to the end of the input as soon
    // as they presss a key due to the currency formatting logic in the onChange
    // handler being triggered and it updating the input to have the new formatted
    // value.
    //
    // What this means is that when a field has a value like '1,000' and the user
    // clicks into the input, the input value will automatically change to '1000'
    // to allow them to edit the unformatted value.
    //
    // If this behaviour proves undesirable from a product POV then we need to
    // update the onChange handler so that it keeps track of the cursor position
    // and calls setSelectionRange(cursorPosition, cursorPosition) after any change
    // is made.

    if (variant === 'currency') {
      const re = new RegExp(getThousandsSeparator(), 'g')
      setValue(name, value.replace(re, ''))
    }
  }

  // Prevents event bubbling on key presses we expect to occur within a text input
  // while editing it.
  const onKeyDownStopPropagation = (e) => {
    const aKeyCode = 65
    const spaceKeyCode = 32

    if (
      e.key === 'ArrowLeft' ||
      e.key === 'ArrowRight' ||
      e.key === 'Backspace' ||
      // Ctrl-A or Cmd-A for selecting all
      (e.ctrlKey && e.keyCode === aKeyCode) ||
      (e.metaKey && e.keyCode === aKeyCode) ||
      e.keyCode === spaceKeyCode
    ) {
      e.stopPropagation()
    }
  }

  return (
    <InternalTextField
      {...props}
      value={value}
      error={error}
      disabled={disabled}
      register={register}
      onChange={
        variant === 'currency' ||
        variant === 'percent' ||
        variant === 'number' ||
        variant === 'hour'
          ? onChangeHandler
          : onChange
      }
      onBlur={
        variant === 'currency' ||
        variant === 'percent' ||
        variant === 'number' ||
        variant === 'hour'
          ? onBlurHandler
          : onBlur
      }
      onFocus={onFocusHandler}
      isFocused={isFocused}
      onKeyDown={isInGrid ? onKeyDownStopPropagation : undefined}
    />
  )
}

const SelfContainedTextField: FC<LoadedTextFieldProps> = (props) => {
  const {
    value,
    onBufferingChange,
    onChange,
    onBlur,
    required,
    variant,
    onlyPostiveNumbers,
  } = props
  const [insertedValue, setInsertedValue] = useState<string>(value || '')
  const [isError, setIsError] = useState<FieldError | undefined>()
  const bufferingTimeout = useRef<ReturnType<typeof setTimeout>>()
  const onChangeCurrencyHandler = (value: string) => {
    //This prevents the user to insert letters and symbols in the input
    const newValue = value.replace(regExps.currencyInput, '')

    return transformTextFieldNumberOnChange(newValue, onlyPostiveNumbers)
  }

  const onChangePercentHandler = (value: string) => {
    //This prevents the user to insert letters and symbols in the input
    const newValue = value.replace(regExps.percentInput, '')

    return transformTextFieldNumberOnChange(newValue, onlyPostiveNumbers)
  }

  const onChangeNumberHandler = (value: string) => {
    //This prevents the user to insert letters and symbols in the input
    const newValue = value.replace(regExps.numberInput, '')

    return transformTextFieldNumberOnChange(newValue, onlyPostiveNumbers)
  }

  const handleOnChange = async (event: any) => {
    let updatedValue = event.target.value

    if (required) {
      const isEmpty = updatedValue.trim() === ''
      setIsError(isEmpty ? { type: 'required' } : undefined)
    }

    if (variant === 'currency') {
      updatedValue = onChangeCurrencyHandler(event.target.value)
    } else if (variant === 'percent') {
      updatedValue = onChangePercentHandler(event.target.value)
    } else if (variant === 'number') {
      updatedValue = onChangeNumberHandler(event.target.value)
    } else if (variant === 'hour') {
      updatedValue = onChangePercentHandler(event.target.value)
    }

    event.target.value = updatedValue
    if (onBufferingChange) {
      if (bufferingTimeout.current) {
        clearTimeout(bufferingTimeout.current)
      }
      bufferingTimeout.current = setTimeout(() => {
        updatedValue = onBufferingChange(event)
      }, LoadedTextFieldBufferingTimeout)
    }
    if (onChange) {
      updatedValue = onChange(event)
    }

    setInsertedValue(updatedValue)
  }

  const handleOnBlur = async (event: any) => {
    let updatedValue = event.target.value

    if (onBlur) {
      updatedValue = onBlur(event)
    }

    setInsertedValue(updatedValue)
  }

  useEffect(() => {
    setInsertedValue(value || '')
  }, [value])

  return (
    <InternalTextField
      {...props}
      value={insertedValue}
      onChange={handleOnChange}
      onBlur={handleOnBlur}
      error={isError}
    />
  )
}

const InternalTextField: FC<
  LoadedTextFieldProps & { error?: FieldError; register?: UseFormRegister<any> }
> = ({
  name,
  value,
  label,
  placeholder,
  type = 'text',
  multiline = false,
  labelBoxProps,
  labelPosition = 'left',
  textFieldSxProps,
  additiveTextFieldSxProps,
  containerSxProps,
  onChange,
  onBlur,
  onFocus,
  onKeyDown,
  error,
  register,
  startAdornment,
  endAdornment,
  size,
  labelSxProps,
  fullWidth,
  disabled,
  inputProps,
  isInGrid = false,
  tabIndex,
  autoComplete,
  inlineInputIcon,
  ...registerOptions
}) => {
  const theme = useTheme<Theme>()

  const inputRef = useRef<HTMLInputElement>(null)

  /**
   * Highlights the input contentwhen the user clicks on the text field
   * so the user can immediately start typing
   */
  const handleClick = () => {
    if (inputRef.current && isInGrid) {
      inputRef.current.select()
    }
  }

  // If the user has specified a inline input icon, check if it should be shown
  // and if so update the text field styles to ensure the icon sits immediately
  // to the right of the input content.
  if (inlineInputIcon?.show()) {
    textFieldSxProps = {
      ...textFieldSxProps,
      '& .MuiInputBase-input': {
        fieldSizing: 'content',
        width: 'unset',
      },
    }
  }

  return (
    <Box
      display={'flex'}
      flex={fullWidth ? 1 : 'unset'}
      flexDirection={labelPosition === 'left' ? 'row' : 'column'}
      alignItems={labelPosition === 'left' ? 'center' : 'self-start'}
      sx={containerSxProps}
    >
      <Box minHeight={'1.884rem'} {...labelBoxProps}>
        {label && (
          <LoadedFormLabel
            label={label}
            labelPosition={labelPosition}
            sx={labelSxProps}
          />
        )}
      </Box>

      <TextField
        id={`text-field-${name}`}
        variant="outlined"
        type={type}
        multiline={multiline}
        disabled={disabled}
        fullWidth={fullWidth}
        tabIndex={tabIndex}
        autoComplete={autoComplete}
        inputRef={inputRef}
        inputProps={inputProps}
        {...(placeholder && { placeholder: placeholder })}
        sx={{
          ...(additiveTextFieldSxProps as any),
          '& .Mui-focused': {
            // make sure that the input has a border when in the grid and focused
            ...(isInGrid && {
              outline: (theme) => `1px solid ${theme.palette.grey[400]}`,
              outlineOffset: '2px',
            }),
            // push the input and the icon apart when focused in the grid with
            // an inline input icon
            ...(isInGrid &&
              inlineInputIcon && { justifyContent: 'space-between' }),

            // we hide the input icon while focused to avoid any overflow issues
            '& .LoadedIcon-with-tooltip': {
              display: 'none',
            },
          },
          '& .MuiInputBase-root': {
            ...(additiveTextFieldSxProps &&
              additiveTextFieldSxProps['& .MuiInputBase-root']),
            ...(!fullWidth && {
              ...(size === 'small' && { width: '10rem' }),
              ...(size === 'large' && { width: '27rem' }),
            }),

            ...(multiline && {
              ...{
                alignItems: 'flex-start !important',
                flexFlow: 'column !important',
              },
            }),
            ...(isInGrid && {
              paddingLeft: '0px',
              '&.Mui-focused': {
                paddingLeft: '12px',
              },
              '.MuiOutlinedInput-notchedOutline': {
                border: 0,
              },
              ...(disabled
                ? {
                    '& .MuiOutlinedInput-input.Mui-disabled': {
                      WebkitTextFillColor: 'unset',
                      color: theme.palette.text.primary,
                    },
                    '.MuiTypography-root': {
                      color: theme.palette.text.primary,
                    },
                  }
                : {
                    '& .MuiInputBase-input': {
                      color: `${theme.palette.primary.main} !important`,
                    },
                  }),
            }),
          },

          ...textFieldSxProps,
        }}
        InputProps={{
          ...({
            ...(startAdornment && {
              startAdornment: (
                <InputAdornment
                  position="start"
                  sx={{
                    ...(isInGrid && { '& p': { color: 'primary.main' } }),
                    marginRight: 0,
                    paddingRight: '0.2rem',
                  }}
                >
                  {startAdornment}
                </InputAdornment>
              ),
            }),
            ...(endAdornment && {
              endAdornment: (
                <InputAdornment position="end">{endAdornment}</InputAdornment>
              ),
            }),
            ...(inlineInputIcon && {
              inputComponent: InputWithInlineIcon,
              // this inputProps is the key to the inputComponent above not re-rendering,
              // we can't have a function for inputComponent to pass props, else each
              // change will cause a re render
              inputProps: {
                ...inputProps,
                inputRef,
                message: inlineInputIcon?.tooltipMessage,
                Icon: inlineInputIcon.show()
                  ? inlineInputIcon?.Icon
                  : undefined,
                iconProps: inlineInputIcon.iconProps,
              },
            }),
          } as any),
        }}
        helperText={error ? error.message : null}
        error={!!error}
        value={value == null ? '' : value}
        {...(error && {
          helperText: error ? error.message : null,
          error: !!error,
        })}
        {...(!register && onChange && { onChange: onChange })}
        {...(!register && onBlur && { onBlur: onBlur })}
        onFocus={onFocus}
        onKeyDown={(event: React.KeyboardEvent) => {
          // Trigger blur when Enter key is pressed - it doesn't matter
          // if the input is in a grid or not
          if (event.key === 'Enter') {
            if (inputRef.current) {
              inputRef.current.blur()
            }
          }
          onKeyDown && onKeyDown(event)
        }}
        {...(register && {
          ...register(name, {
            onChange: onChange,
            onBlur: onBlur,
            ...registerOptions,
          }),
        })}
        onClick={handleClick}
      />
    </Box>
  )
}
